'use client';

import dynamic from 'next/dynamic';
import { FC, PropsWithChildren, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';

import { productDetailPayloadToBaseCarData } from '@/data/ProductApi/ProductDetailPayloadToBaseCarData';
import { CarPriceCard } from '@/pdp/components/CarPriceCard/CarPriceCard';
import { CarStickyBarContent } from '@/pdp/components/CarStickyBar/CarStickyBarContent';
import { PdpLayout } from '@/pdp/layouts/PdpLayout/PdpLayout';
import { checkCarStatus } from '@/plp/components/Results/useUpdatedCarStatus';
import { authStore } from '@/stores/authStore';
import { carStatusStore } from '@/stores/carStatusStore';
import { cartStore } from '@/stores/cartStore';
import { pdpStore } from '@/stores/pdpStore';
import { ProductDetailsPayload } from '@/types/ProductDetail.type';
import { pushToDataLayer } from '@/utils/pushToDataLayer';

import { CarDisplayState, formatCarDisplayState } from './pdpHelpers';

const CarStickyBar = dynamic(
  () => import('@/pdp/components/CarStickyBar/CarStickyBar').then((mod) => mod.CarStickyBar),
  { ssr: false },
);

type GalleryDisplay = 'view360' | 'gallery' | 'imperfections';

export interface GalleryModalState {
  displayState: GalleryDisplay;
  openDoors: boolean;
  exterior: boolean;
  galleryStartIndex?: number;
}

interface Props {
  productDetails: ProductDetailsPayload;
  hero: ReactNode;
  coreValues: ReactNode;
  gallery: ReactNode;
}

export const Pdp: FC<PropsWithChildren<Props>> = ({ productDetails, hero, coreValues, gallery, children }) => {
  const currentCarStatus = useRef<CarDisplayState>('loading');
  const { unavailableCarsLoading, carStatus } = carStatusStore(
    (state) => ({
      unavailableCarsLoading: state.unavailableCarsLoading,
      carStatus: checkCarStatus(state.unavailableCarsData[productDetails.sku]) ?? productDetails.state,
    }),
    shallow,
  );
  const { isUserLoggedIn, userDataLoading, userOrdersLoading, userOrders } = authStore(
    (state) => ({
      isUserLoggedIn: state.isUserLoggedIn,
      userDataLoading: state.userDataLoading,
      userOrdersLoading: state.userOrdersLoading,
      userOrders: state.userOrders,
    }),
    shallow,
  );
  const { carLoading, carData, cartExpired, apiCartData, apiCartLoading } = cartStore(
    (state) => ({
      carLoading: state.carLoading,
      carData: state.carData,
      cartExpired: state.cartExpired,
      apiCartData: state.apiCartData,
      apiCartLoading: state.apiCartLoading,
    }),
    shallow,
  );
  const { setCurrentModal } = pdpStore(
    ({ setCurrentModal }) => ({
      setCurrentModal,
    }),
    shallow,
  );
  const [carDisplayState, setCarDisplayState] = useState<CarDisplayState>('loading');

  const isCheckoutProgress = useMemo(
    () => !carLoading && !!carData && !cartExpired,
    [carLoading, carData, cartExpired],
  );
  const isLoadingData = useMemo(
    () => unavailableCarsLoading || userDataLoading || userOrdersLoading || apiCartLoading || carLoading,
    [unavailableCarsLoading, userDataLoading, userOrdersLoading, apiCartLoading, carLoading],
  );

  useEffect(() => {
    currentCarStatus.current = 'loading';
    setCarDisplayState('loading');
    setCurrentModal(null);

    return () => {
      setCurrentModal(null);
    };
  }, [productDetails.sku, setCurrentModal]);

  useEffect(() => {
    // Only update the car display state if we're not loading the user data
    if (!isLoadingData) {
      const carDisplayState = formatCarDisplayState(productDetails.sku, apiCartData, userOrders, carStatus);
      if (carDisplayState !== currentCarStatus.current) {
        currentCarStatus.current = carDisplayState;
        setCarDisplayState(carDisplayState);
        if (carDisplayState === 'not-available' && (carStatus === 'draftOrder' || carStatus === 'reserved')) {
          setCurrentModal({ name: 'purchaseInProgress' });
          pushToDataLayer({
            event: 'pdp_interaction',
            type: 'view',
            value: 'car_reserved',
          });
        }
      }
    }
  }, [productDetails.sku, apiCartData, isUserLoggedIn, userOrders, carStatus, isLoadingData]);

  return (
    <>
      <PdpLayout
        hero={hero}
        price={
          <>
            <CarPriceCard
              productDetails={productDetails}
              displayState={carDisplayState}
              isCheckoutProgress={isCheckoutProgress}
            />
            {coreValues}
          </>
        }
        gallery={gallery}
      >
        {children}
      </PdpLayout>

      <CarStickyBar>
        <CarStickyBarContent
          isCheckoutProgress={isCheckoutProgress}
          carData={productDetailPayloadToBaseCarData(productDetails)}
          displayState={carDisplayState}
        />
      </CarStickyBar>
    </>
  );
};
